import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import EUserType from 'enums/userTypes';
import * as jose from 'jose';
import i18n from 'locales/i18n';
import { call, put } from 'redux-saga/effects';

import { authHttpClient, httpClient, rikHttpClient } from 'services/httpClient';
import ICreatePasswordResponse from 'services/httpClient/responses/auth/ICreatePasswordResponse';
import IGetTraderProfileResponse from 'services/httpClient/responses/auth/IGetTraderProfileResponse';

import { authActions } from '../index';
import { ICreatePasswordRequestAction } from '../reducers/createPassword/request';

function* createPassword(action: PayloadAction<ICreatePasswordRequestAction>) {
  try {
    // Set password for new account
    const body = {
      reset_password_token: action.payload.data.createPasswordToken,
      password: action.payload.data.password,
      password_confirmation: action.payload.data.verifyPassword,
    };
    authHttpClient.defaults.headers.common['access-token'] =
      action.payload.data.userData.token;
    authHttpClient.defaults.headers.common.client =
      action.payload.data.userData.client;
    authHttpClient.defaults.headers.common.uid =
      action.payload.data.userData.uid;

    const { data }: AxiosResponse<ICreatePasswordResponse> = yield call(
      authHttpClient.put,
      'auth/password',
      body,
    );

    const { data: accessToken }: AxiosResponse<string> = yield call(
      authHttpClient.post,
      'api/users/login',
      {
        email: data.data.email,
        password: action.payload.data.password,
      },
    );

    httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    rikHttpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    yield put(authActions.signInSuccess());
    yield put(
      authActions.setToken({
        accessToken,
      }),
    );

    const claims: jose.JaimyJWTPayload = jose.decodeJwt(accessToken);
    i18n.changeLanguage(claims.locale);

    if (!claims.rik_pp_access) {
      action.payload.functions.error({
        response: { data: { error: 'access-denied' } },
      });
      yield put(authActions.logoutRequest());
    }
    // Check if user is owner or worker
    if (claims.trader_id !== null) {
      const { data }: AxiosResponse<IGetTraderProfileResponse> = yield call(
        httpClient.get,
        `/traders/v1/traders/${claims.trader_id}`,
      );

      const { data: interventionPartnershipData }: AxiosResponse = yield call(
        httpClient.get,
        `/backoffice/v1/intervention_partnerships/${data.intervention_partnership_id}`,
      );

      data.role = EUserType.Owner;
      data.all_interventions = true;
      data.company_details = true;
      data.invoice_permission = true;
      data.user_management = true;
      data.trader_id = data.id;

      // set company address
      data.address = `${interventionPartnershipData.address.street} ${interventionPartnershipData.address.number}`;
      data.city = interventionPartnershipData.city;
      data.zip = interventionPartnershipData.zip;
      data.phone = interventionPartnershipData.phone;
      data.iban = (interventionPartnershipData.iban || data.iban).replaceAll(
        ' ',
        '',
      );

      yield put(authActions.setProfile(data));
    } else if (claims.worker_id !== null) {
      // get worker info
      const { data }: AxiosResponse<IGetTraderProfileResponse> = yield call(
        httpClient.get,
        `/rik/v1/workers/${claims.worker_id}`,
      );

      const { data: interventionPartnershipData }: AxiosResponse = yield call(
        httpClient.get,
        `/backoffice/v1/intervention_partnerships/${data.intervention_partnership_id}`,
      );

      // check role of worker
      if (data.admin) {
        data.role = EUserType.Admin;
      } else if (data.accountant) {
        data.role = EUserType.Accountant;
      } else if (data.technician) {
        data.role = EUserType.Technician;
      }

      // set company address
      data.address = `${interventionPartnershipData.address.street} ${interventionPartnershipData.address.number}`;
      data.city = interventionPartnershipData.city;
      data.zip = interventionPartnershipData.zip;
      data.phone = interventionPartnershipData.phone;

      yield put(authActions.setProfile(data));
    }

    action.payload.functions.navigateTo();
  } catch (error) {
    action.payload.functions.error(error);
    yield put(authActions.createPasswordFailure());
  }
}

export default createPassword;
